import type { Nullable } from "@core/domain/types/nullable.type";
import { normalizeText } from "@core/domain/utils/normalize-text";
import { BaseViewModel } from "@iot-platform/core";
import {
    SpatialLevel,
    SpatialLevels,
} from "@station/domain/models/spatial-level.model.ts";
import type { StationListSearchFilters } from "@station/domain/models/station.model.ts";
import { SpatialLevelGetAllUseCase } from "@station/domain/usecases/spatial-level-get-all.usecase";
import { getInitialSuggestions } from "@station/presentation/utils/station-suggestions.utils";
import type { SearchResult } from "@weather/presentation/pages/weather/weather.page";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable } from "mobx";

@injectable()
export class AgentSearchMobileViewmodel extends BaseViewModel {
    @observable
    spatialLevels: SpatialLevels = SpatialLevels.empty();

    initialFiltersValue: StationListSearchFilters = {
        search: "",
    };

    @observable
    filters: StationListSearchFilters = this.initialFiltersValue;

    @observable
    searchResults: {
        id: number;
        label: string;
        isStation?: boolean;
        latitude?: Nullable<string>;
        longitude?: Nullable<string>;
        icon?: string;
    }[] = [];

    constructor(
        @inject(SpatialLevelGetAllUseCase)
        private readonly spatialLevelGetAllUseCase: SpatialLevelGetAllUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await this.fetchSpatialLevels();
    }

    private async fetchSpatialLevels(): Promise<void> {
        this.spatialLevels = await this.spatialLevelGetAllUseCase.execute();
    }

    @action
    setFilters(filters: { search: string }): void {
        this.filters = filters;
        this.updateSearchResults();
    }

    // Updates the search results based on the current search query
    @action
    updateSearchResults(): void {
        const query = normalizeText(this.filters.search);

        if (!query) {
            this.searchResults = getInitialSuggestions(
                this.spatialLevels.spatialLevels,
            );
            return;
        }

        const searchResults = new Map<number, SearchResult>();

        // Recursive function to search through spatial levels and stations
        const processLevel = (level: Readonly<SpatialLevel>): void => {
            level.stations.forEach((station) => {
                if (normalizeText(station.displayName).includes(query)) {
                    searchResults.set(station.id.valueOrThrow, {
                        label: station.displayName,
                        id: station.id.valueOrThrow,
                        icon: station.icon,
                        latitude: station.latitude.length
                            ? station.latitude
                            : null,
                        longitude: station.longitude,
                    });
                }
            });

            level.children?.forEach(processLevel);
        };

        // Iterate through all spatial levels and process them
        this.spatialLevels.spatialLevels.forEach(processLevel);

        // Convert results map to an array and update searchResults
        this.searchResults = Array.from(searchResults.values());
    }
}
